import Backbone from 'backbone'
import template from './historical.hbs'
import noChartTemplate from './historical-no-chart.hbs'
import _ from 'lodash'
import ColorLegend from 'scoreboard/charts/color-legend'
import shouldRenderLegend, {
	CHART_TITLE
} from 'scoreboard/charts/should-render-legend'
import highlight from 'scoreboard/components/highlight'
import i18n from 'scoreboard/components/i18n'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import appDisplay from 'scoreboard/components/app-display'
import ScorecardChartDialogEditor from 'scoreboard/charts/edit/scorecard-chart-dialog-editor'
import ChartDesigner from 'scoreboard/charts/model/chart-designer'
import renderChartDesigner from 'scoreboard/charts/render-chart-designer'
import ChartData from 'scoreboard/charts/chart-data/chart-data'
import unwrapModel from 'scoreboard/components/unwrap-model'
import { incompleteReplicationContent } from 'scoreboard/charts/charts-view'
import constants from 'scoreboard/components/constants'
const { DOCKING } = constants
import { isV5 } from 'scoreboard/components/version'

var debug = false

function points(scores, type) {
	var points = _.map(scores, function(score) {
		var raw = type === 'metric' ? score.actual : score.score
		return {
			raw: raw,
			color: score.color,
			domainId: score.periodId
		}
	}.bind(this))

	return points
}

/**
 * returns true if the scorecard model is a docking node
 */
function isDockingNode(model) {
	model = unwrapModel(model)
	return model.type === DOCKING
}

var HistoricalPerformance = Backbone.View.extend({

	/** @type ScorecardNode */
	model: null,

	className: 'panel',

	events: {
		'click .chart-editor a': 'onClickChartEditor'
	},

	initialize: function() {
		this.on('resize', this.renderChart)
	},

	render: function() {
		if (this.$('.chart-container').length === 0) {
			this.$el.empty().append(template(this.model.attributes))
			if (isV5()) {
				this.$('.panel-header').addClass('panel-header-transparent')
			}
		}

		this.secure()

		process.nextTick(function () {
			this.renderColorLegend()
			this.renderChart()
		}.bind(this))

		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		return this
	},

	renderColorLegend: function () {
		if (this.model.get('chart').incompleteReplication) {
			return
		}
		const chart = new ChartDesigner(this.model.get('chart'))
		this._colorLegend && this._colorLegend.remove()
		if (shouldRenderLegend(chart, CHART_TITLE)) {
			chart.data = new ChartData({ data: this.model.get('chartData')})
			this._colorLegend = new ColorLegend({
				tooltip: true,
				chart,
				legendType: 'chart-title'
			})

			this.$('.panel-header h3').append(this._colorLegend.el)
			this._colorLegend.render()
			this._colorLegend.on('emphasize deemphasize', this.emphasize.bind(this))
		}
	},

	/**
	 * Renders the actual SVG chart.
	 *
	 * @param {Object} opt
	 *
	 * @param {Boolean} [fresh] if true, destroys the chart before re-rendering it
	 */
	renderChart: function(opt = {}) {
		// for docking nodes, we are showing a message, so we don't need
		// to render the chart
		if (isDockingNode(this.model)) return

		let container = this.$('.chart-container')

		if (this.model.get('chart').incompleteReplication) {
			let emptyState = incompleteReplicationContent().render()
			container.html(emptyState.el)
			return this
		}

		let chartData = new ChartData({ data: this.model.get('chartData') })
		let chartDesigner = new ChartDesigner(this.model.get('chart'), {
			data: chartData
		})

		let unscoredText = this.model.scoringType()?.unscoredText
		let seriesCount = chartDesigner.getSerieFeatures().length
		let hasNoSeries = seriesCount === 0
		this.$el.closest('.row').toggleClass('has-no-chart', hasNoSeries)

		if (unscoredText && hasNoSeries) {
			// #47453 we can't chart unscored text metrics, so the default
			// chart generated in ChartDesignerDefaultBuilder will be empty.
			// Display a message explaining why.
			this.renderUnscoredTextMessage(container)
			return this
		} else if (hasNoSeries) {
			this.renderNoDataSeriesMessage(container)
			return this
		} else {
			this.$('.empty-state-body').remove()
		}

		this.chartView && this.chartView.destroyTooltip()
		if (opt.fresh && this.chartView) {
			this.chartView.destroy()
			delete this.chartView
		}

		this.chartView = renderChartDesigner({
			chartDesigner,
			chartData,
			container,
			emphasize: opt.emphasize
		})
		this.chartDesigner = chartDesigner
	},

	renderUnscoredTextMessage: function (container) {
		let message = i18n.get('scorecards.overview.noChartForScoringType')

		let clickEditMessage
		if (this.model.overviewEditable()) {
			clickEditMessage = i18n.get('scorecards.overview.noChartForScoringType.clickEdit',
				highlight(i18n.get('common.edit')))
		}

		let emptyState = noChartTemplate({ message, clickEditMessage })

		container.html(emptyState)
	},

	renderNoDataSeriesMessage: function(container) {
		let message = i18n.get('scorecards.overview.noDataSeries')

		let clickEditMessage
		if (this.model.overviewEditable()) {
			clickEditMessage = i18n.get('scorecards.overview.noDataSeries.clickEdit',
				highlight(i18n.get('common.edit')))
		}

		let emptyState = noChartTemplate({ message, clickEditMessage })

		container.html(emptyState)
	},

	emphasize: function (seriesId) {
		this.renderChart({
			emphasize: seriesId
		})
	},

	onClickChartEditor: function () {
		if (!this.model.overviewEditable()) { return }

		const chartData = new ChartData({
			data: _.cloneDeep(this.model.get('chartData'))
		})
		const chartDesigner = new ChartDesigner(
			_.cloneDeep(this.model.get('chart')), {
			data: chartData
		})

		const chartDialogEditor = new ScorecardChartDialogEditor({
			model: chartDesigner,
			chartData,
			scorecardNodeId: this.model.get('id'),
			width: this.$el.width(),
			height: this.$el.height()
		})

		appDisplay.dialog(chartDialogEditor)

		chartDialogEditor.once('save', ({ chartDesigner, chartData }) => {
			this.model.set('chart', unwrapModel(chartDesigner))
			this.model.set('chartData', chartData._data)
			this.renderColorLegend()
			this.renderChart({ fresh: true })
		})

		// #59778 update the chart after resetting to default chart settings
		chartDialogEditor.once('reset', ({ chartDesigner, chartData }) => {
			this.model.set('chart', unwrapModel(chartDesigner))
			this.model.set('chartData', chartData)
			this.renderColorLegend()
			this.renderChart({ fresh: true })
		})
	},

	secure: function () {
		this.$('.chart-editor').toggleClass('is-not-displayed', !this.model.overviewEditable())
	},

	removeConfig: function () {
		this.tooltip && this.tooltip.remove()
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this.covertButtonTooltips, 'remove')
		this._colorLegend && this._colorLegend.remove()
		this.chartView && this.chartView.destroy()
	}
})

HistoricalPerformance.isDockingNode = isDockingNode
HistoricalPerformance.points = points

export default HistoricalPerformance
export { points }
export { isDockingNode }

HistoricalPerformance.__filename__ = 'scorecards/overview/historical.js'
